/**
 * Single-flight gate on player actions.
 *
 * On a slow network a tap looks like it did nothing, so the player taps again —
 * and a second `play` / `call` / `decide` goes out before the first snapshot
 * lands. The server is defensive, but the honest fix is that the second tap
 * never fires. One global flag rather than one per action: a person can only
 * mean one action at a time, and any authoritative response clears the gate.
 */
import { useEffect, useState } from 'react'
import { ServerEvent } from '@cards/shared'
import type { Socket } from 'socket.io-client'

const SAFETY_TIMEOUT_MS = 5000

let locked = false
let timeoutId: number | null = null
let installedOn: Socket | null = null
const subscribers = new Set<() => void>()

function setLocked(next: boolean): void {
  if (locked === next) return
  locked = next
  for (const fn of subscribers) fn()
}

function unlock(): void {
  if (timeoutId !== null) {
    window.clearTimeout(timeoutId)
    timeoutId = null
  }
  setLocked(false)
}

/** Anything from this set means the server has spoken and the gate can open. */
const UNLOCK_EVENTS = new Set<string>([
  ServerEvent.Snapshot,
  ServerEvent.Error,
  ServerEvent.TableJoined,
  ServerEvent.TableCreated,
  ServerEvent.SessionInvalid,
  ServerEvent.Kicked,
])

/** Subscribed via `onAny`, not per-event `socket.on`: components call
 *  `socket.off(EVENT)` with no handler in their cleanup, which socket.io reads
 *  as "remove every listener for this event" and would silently strip the
 *  unlock handler — leaving the UI gated until the 5 s safety timer. */
export function installActionLock(socket: Socket): void {
  if (installedOn === socket) return
  installedOn = socket
  socket.onAny((event: string) => {
    if (UNLOCK_EVENTS.has(event)) unlock()
  })
  socket.on('disconnect', unlock)
}

export function lockAction(): void {
  setLocked(true)
  if (timeoutId !== null) window.clearTimeout(timeoutId)
  timeoutId = window.setTimeout(() => {
    timeoutId = null
    setLocked(false)
  }, SAFETY_TIMEOUT_MS)
}

/** Wrap an emit so the gate closes once for the whole gesture. Returns false
 *  (and does nothing) when another action is already in flight. */
export function runGuarded(fn: () => void): boolean {
  if (locked) return false
  fn()
  lockAction()
  return true
}

export function isActionLocked(): boolean {
  return locked
}

export function useActionLock(): boolean {
  const [v, setV] = useState(locked)
  useEffect(() => {
    const fn = () => setV(locked)
    subscribers.add(fn)
    fn()
    return () => {
      subscribers.delete(fn)
    }
  }, [])
  return v
}

/** Only true once the gate has been held for `delayMs`, so a normal round trip
 *  never flickers a disabled button; a genuinely slow one still gives feedback. */
export function useActionLockDelayed(delayMs = 220): boolean {
  const [v, setV] = useState(false)
  useEffect(() => {
    let timer: number | null = null
    const fn = () => {
      if (locked) {
        if (timer !== null) return
        timer = window.setTimeout(() => {
          timer = null
          setV(true)
        }, delayMs)
      } else {
        if (timer !== null) window.clearTimeout(timer)
        timer = null
        setV(false)
      }
    }
    subscribers.add(fn)
    fn()
    return () => {
      if (timer !== null) window.clearTimeout(timer)
      subscribers.delete(fn)
    }
  }, [delayMs])
  return v
}
